import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, CheckCircle, XCircle, AlertCircle, User, 
  Calendar, Pen, FileText, Activity, Clock, ExternalLink, Video, X, Maximize2, Minimize2, Download, Loader2, Lock, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import UpcomingBookings from '../components/UpcomingBookings';
import Leaderboards from '../components/Leaderboards';
import WeeklyTrainingPlan from '../components/WeeklyTrainingPlan';
import VideoFootage from '../components/VideoFootage';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const FORM_TYPE_ICONS = {
  medical: '🏥',
  photo_video: '📸',
  training: '🏃',
  data_processing: '🔒'
};

export default function ParentPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completedForms, setCompletedForms] = useState([]);
  
  // Account creation state
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  
  // Google Meet state
  const [meetUrl, setMeetUrl] = useState(null);
  const [meetExpanded, setMeetExpanded] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  
  const [formData, setFormData] = useState({
    guardian_name: '',
    guardian_email: '',
    guardian_phone: '',
    guardian_relationship: 'Parent',
    additional_data: {}
  });

  const validateToken = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parent-portal/validate/${token}`);
      setPortalData(response.data);
      
      // Track already signed forms
      const existing = response.data.existing_consents?.map(c => c.form_type) || [];
      setCompletedForms(existing);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const downloadReport = async () => {
    if (!portalData?.child?.id) return;
    
    setDownloadingReport(true);
    try {
      // First, save the report to documents
      const saveResponse = await fetch(`${API}/reports/portal/${portalData.child.id}/report-card/save`, {
        method: 'POST',
        headers: {
          'X-Portal-Token': token
        }
      });
      
      if (saveResponse.ok) {
        const saveResult = await saveResponse.json();
        toast.success('Report saved to Media tab!');
        
        // Download the saved file
        const downloadResponse = await fetch(`${API}/children/${portalData.child.id}/documents/${saveResult.document.id}/download`, {
          headers: {
            'X-Portal-Token': token
          }
        });
        
        if (downloadResponse.ok) {
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = saveResult.document.original_filename;
          a.click();
          window.URL.revokeObjectURL(url);
          toast.success('Report downloaded successfully!');
        }
      } else {
        // Fallback to direct download
        const response = await fetch(`${API}/reports/portal/${portalData.child.id}/report-card`, {
          headers: {
            'X-Portal-Token': token
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `report_${portalData.child.first_name}_${portalData.child.last_name}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          toast.success('Report downloaded successfully!');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to download report');
        }
      }
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  // Create parent account from portal
  const createParentAccount = async () => {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setCreatingAccount(true);
    try {
      // Create the parent account using the portal token
      const response = await axios.post(`${API}/auth/create-parent-from-portal`, {
        portal_token: token,
        password: password
      });
      
      if (response.data.access_token) {
        // Log the user in using setAuth
        setAuth(response.data.user, response.data.access_token);
        
        toast.success('Account created successfully!');
        
        // Navigate to the child's profile
        navigate(`/child/${portalData.child.id}`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create account';
      toast.error(message);
    } finally {
      setCreatingAccount(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  useEffect(() => {
    if (selectedFormType && signatureType === 'drawn') {
      setTimeout(initCanvas, 100);
    }
  }, [selectedFormType, signatureType]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
  };

  const getSignatureData = () => {
    if (signatureType === 'typed') {
      return typedSignature;
    }
    const canvas = canvasRef.current;
    return canvas ? canvas.toDataURL('image/png') : '';
  };

  const signForm = async (e) => {
    e.preventDefault();
    
    const signatureData = getSignatureData();
    if (!signatureData || (signatureType === 'typed' && !typedSignature.trim())) {
      toast.error('Please provide a signature');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/parent-portal/sign/${token}`, {
        child_id: portalData.child.id,
        form_type: selectedFormType,
        guardian_name: formData.guardian_name,
        guardian_email: formData.guardian_email,
        guardian_phone: formData.guardian_phone,
        guardian_relationship: formData.guardian_relationship,
        signature_data: signatureData,
        signature_type: signatureType,
        additional_data: formData.additional_data,
        agreed_terms: true
      });
      
      toast.success('Consent form signed successfully!');
      setCompletedForms([...completedForms, selectedFormType]);
      setSelectedFormType(null);
      
      // Reset form
      setFormData({
        guardian_name: formData.guardian_name, // Keep guardian info
        guardian_email: formData.guardian_email,
        guardian_phone: formData.guardian_phone,
        guardian_relationship: formData.guardian_relationship,
        additional_data: {}
      });
      setTypedSignature('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500 animate-pulse">Validating link...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <XCircle className="h-16 w-16 text-[#ff025f] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Link Invalid</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            Please contact your coach for a new consent form link.
          </p>
        </div>
      </div>
    );
  }

  const child = portalData?.child;
  const formTypes = portalData?.form_types || {};
  const requiredTypes = ['medical', 'training', 'data_processing'];
  const allRequiredSigned = requiredTypes.every(t => completedForms.includes(t));

  return (
    <div className="min-h-screen bg-black" data-testid="parent-portal">
      {/* Header */}
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">BE ELITE APP</h1>
              <p className="text-sm text-gray-400">Parent Consent Portal</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Child Info Card */}
        <div className="bg-zinc-900 rounded-2xl shadow-lg p-6 mb-8 border border-zinc-800">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#02b5ff]/10 to-[#02b5ff]/20 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-[#02b5ff]" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">
                {child?.first_name} {child?.last_name}
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  DOB: {child?.date_of_birth ? new Date(child.date_of_birth).toLocaleDateString() : 'N/A'}
                </span>
                <span className="capitalize">{child?.gender}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Download Report Button - only show when DOB is set */}
              {child?.date_of_birth && (
                <button
                  onClick={downloadReport}
                  disabled={downloadingReport}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#ff025f] to-[#cc0050] hover:from-[#cc0050] hover:to-[#aa0040] text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                  data-testid="download-report-btn"
                >
                  {downloadingReport ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download Report
                </button>
              )}
              <button
                onClick={() => setShowAccountSetup(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-lg transition-all"
                data-testid="view-profile-btn"
              >
                <User className="h-4 w-4" />
                View Full Profile
              </button>
            </div>
          </div>
        </div>

        {/* Status Banner */}
        <div className={`rounded-2xl p-6 mb-8 ${allRequiredSigned ? 'bg-[#02b5ff]/10 border border-[#02b5ff]/30' : 'bg-[#ff025f]/10 border border-[#ff025f]/30'}`}>
          <div className="flex items-center gap-3">
            {allRequiredSigned ? (
              <CheckCircle className="h-8 w-8 text-[#02b5ff]" />
            ) : (
              <AlertCircle className="h-8 w-8 text-[#ff025f]" />
            )}
            <div>
              <h3 className={`font-bold ${allRequiredSigned ? 'text-[#02b5ff]' : 'text-[#ff025f]'}`}>
                {allRequiredSigned ? 'All Required Consents Signed!' : 'Required Consents Pending'}
              </h3>
              <p className={`text-sm ${allRequiredSigned ? 'text-[#02b5ff]' : 'text-[#ff025f]'}`}>
                {allRequiredSigned 
                  ? 'Thank you for completing all required consent forms.'
                  : `Please sign the required consent forms below to allow your child to participate.`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Create Account CTA / Inline Form */}
        <div className="rounded-2xl p-6 mb-8 bg-gradient-to-r from-[#02b5ff]/10 to-[#ff025f]/10 border border-zinc-700">
          {!showAccountSetup ? (
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">View & Edit Full Profile</h3>
                  <p className="text-sm text-zinc-400">
                    Create a parent account to view {child?.first_name}'s complete profile, track progress, and update information.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowAccountSetup(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-xl transition-all shadow-lg shadow-[#02b5ff]/20"
                data-testid="create-account-cta-btn"
              >
                <Lock className="h-5 w-5" />
                Set Up Password
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center">
                  <Lock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Create Your Account</h3>
                  <p className="text-sm text-zinc-400">
                    Set a password for <span className="text-[#02b5ff]">{portalData?.parent_email}</span>
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Label className="text-sm font-semibold text-gray-300">Password *</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 rounded-xl pr-10"
                      placeholder="Min 8 characters"
                      data-testid="account-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Confirm Password *</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    placeholder="Re-enter password"
                    data-testid="account-confirm-password-input"
                  />
                </div>
              </div>
              
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-[#ff025f]">Passwords do not match</p>
              )}
              {password && password.length < 8 && (
                <p className="text-sm text-amber-400">Password must be at least 8 characters</p>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAccountSetup(false);
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1 rounded-xl"
                  data-testid="cancel-account-setup-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createParentAccount}
                  disabled={creatingAccount || password.length < 8 || password !== confirmPassword}
                  className="flex-1 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                  data-testid="create-account-submit-btn"
                >
                  {creatingAccount ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 mr-2" />
                      Create Account & Sign In
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Form Selection */}
        {!selectedFormType ? (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-white">Select Consent Form to Sign</h3>
            
            {Object.entries(formTypes).map(([key, value]) => {
              const isSigned = completedForms.includes(key);
              const isRequired = requiredTypes.includes(key);
              
              return (
                <div
                  key={key}
                  className={`bg-zinc-900 rounded-2xl p-5 border-2 transition-all ${
                    isSigned 
                      ? 'border-[#02b5ff]/30 bg-[#02b5ff]/10' 
                      : 'border-zinc-800 hover:border-[#02b5ff]/50 hover:shadow-md cursor-pointer'
                  }`}
                  onClick={() => !isSigned && setSelectedFormType(key)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{FORM_TYPE_ICONS[key] || '📄'}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white">{value.name}</h4>
                          {isRequired && !isSigned && (
                            <span className="px-2 py-0.5 bg-[#ff025f]/10 text-[#ff025f] text-xs font-medium rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{value.description}</p>
                      </div>
                    </div>
                    {isSigned ? (
                      <CheckCircle className="h-8 w-8 text-[#02b5ff]" />
                    ) : (
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={(e) => { e.stopPropagation(); setSelectedFormType(key); }}
                      >
                        Sign Now
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Sign Form */
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">{FORM_TYPE_ICONS[selectedFormType] || '📄'}</span>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {formTypes[selectedFormType]?.name}
                </h3>
                <p className="text-sm text-gray-500">
                  {formTypes[selectedFormType]?.description}
                </p>
              </div>
            </div>

            <form onSubmit={signForm} className="space-y-5">
              {/* Guardian Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Your Full Name *</Label>
                  <Input
                    value={formData.guardian_name}
                    onChange={(e) => setFormData({...formData, guardian_name: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    placeholder="Your legal name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Relationship to Athlete</Label>
                  <Select value={formData.guardian_relationship} onValueChange={(v) => setFormData({...formData, guardian_relationship: v})}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-zinc-700">
                      <SelectItem value="Parent">Parent</SelectItem>
                      <SelectItem value="Guardian">Legal Guardian</SelectItem>
                      <SelectItem value="Carer">Carer</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Email Address</Label>
                  <Input
                    type="email"
                    value={formData.guardian_email}
                    onChange={(e) => setFormData({...formData, guardian_email: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Phone Number</Label>
                  <Input
                    value={formData.guardian_phone}
                    onChange={(e) => setFormData({...formData, guardian_phone: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    placeholder="+44 7xxx xxx xxx"
                  />
                </div>
              </div>

              {/* Medical Form Additional Fields */}
              {selectedFormType === 'medical' && (
                <div className="p-4 bg-[#ff025f]/10 rounded-xl border border-[#ff025f]/30 space-y-4">
                  <h4 className="font-semibold text-[#ff025f]">Medical Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-[#ff025f]">Emergency Contact Name *</Label>
                      <Input
                        value={formData.additional_data.emergency_contact_name || ''}
                        onChange={(e) => setFormData({
                          ...formData, 
                          additional_data: {...formData.additional_data, emergency_contact_name: e.target.value}
                        })}
                        className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-[#ff025f]">Emergency Contact Phone *</Label>
                      <Input
                        value={formData.additional_data.emergency_contact_phone || ''}
                        onChange={(e) => setFormData({
                          ...formData, 
                          additional_data: {...formData.additional_data, emergency_contact_phone: e.target.value}
                        })}
                        className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-[#ff025f]">Known Medical Conditions</Label>
                    <Input
                      value={formData.additional_data.medical_conditions || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        additional_data: {...formData.additional_data, medical_conditions: e.target.value}
                      })}
                      className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                      placeholder="None, or list conditions..."
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#ff025f]">Allergies</Label>
                    <Input
                      value={formData.additional_data.allergies || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        additional_data: {...formData.additional_data, allergies: e.target.value}
                      })}
                      className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                      placeholder="None, or list allergies..."
                    />
                  </div>
                </div>
              )}

              {/* Signature */}
              <div>
                <Label className="text-sm font-semibold text-gray-300">Your Signature *</Label>
                <div className="flex gap-2 mt-2 mb-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={signatureType === 'drawn' ? 'default' : 'outline'}
                    onClick={() => setSignatureType('drawn')}
                    className="rounded-full"
                  >
                    <Pen className="h-4 w-4 mr-1" />
                    Draw
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={signatureType === 'typed' ? 'default' : 'outline'}
                    onClick={() => setSignatureType('typed')}
                    className="rounded-full"
                  >
                    Type
                  </Button>
                </div>

                {signatureType === 'drawn' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 bg-zinc-800">
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={150}
                      className="w-full bg-zinc-900 rounded-lg cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={clearSignature}
                      className="mt-2 text-gray-500"
                    >
                      Clear Signature
                    </Button>
                  </div>
                ) : (
                  <Input
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 rounded-xl text-xl italic text-[#02b5ff]"
                    placeholder="Type your full name"
                    style={{ fontFamily: 'cursive' }}
                  />
                )}
              </div>

              {/* Terms */}
              <div className="p-4 bg-zinc-800 rounded-xl text-sm text-gray-400">
                <p className="font-semibold mb-2">By signing, I confirm that:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>I am the parent/legal guardian of {child?.first_name} {child?.last_name}</li>
                  <li>All information provided is accurate to the best of my knowledge</li>
                  <li>I have read and understood the terms of this consent</li>
                  <li>This digital signature is legally binding</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedFormType(null)}
                  className="flex-1 rounded-xl"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.guardian_name}
                  className="flex-1 bg-[#02b5ff] hover:bg-indigo-700 text-white rounded-xl"
                >
                  {submitting ? 'Signing...' : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Sign Consent
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Expiry Notice */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <Clock className="h-4 w-4 inline mr-1" />
          This link expires on {portalData?.expires_at ? new Date(portalData.expires_at).toLocaleDateString() : 'N/A'}
        </div>

        {/* Upcoming Bookings */}
        {child?.id && (
          <div className="mt-8 space-y-6">
            {/* Weekly Training Plan - Parents can add/edit */}
            <WeeklyTrainingPlan 
              childId={child.id}
              childName={`${child.first_name} ${child.last_name}`}
              canEdit={true}
            />
            
            {/* Video Footage - Parents can add match/training videos */}
            <VideoFootage 
              childId={child.id}
              childName={`${child.first_name} ${child.last_name}`}
              canEdit={true}
            />
            
            <UpcomingBookings 
              childId={child.id}
              onJoinMeet={(url) => setMeetUrl(url)}
            />
            {/* Leaderboards for child's age */}
            <Leaderboards 
              childId={child.id}
              childAge={Math.floor((new Date() - new Date(child.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))}
              isCoach={false}
            />
          </div>
        )}
      </div>

      {/* Google Meet Embedded Window */}
      {meetUrl && (
        <div className={`fixed ${meetExpanded ? 'inset-4' : 'bottom-4 right-4 w-[500px] h-[400px]'} bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl z-50 overflow-hidden transition-all`} data-testid="meet-window">
          <div className="flex items-center justify-between p-3 bg-zinc-800 border-b border-zinc-700">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-green-400" />
              <span className="text-sm font-medium text-white">Google Meet</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMeetExpanded(!meetExpanded)}
                className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                title={meetExpanded ? 'Minimize' : 'Maximize'}
              >
                {meetExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <a
                href={meetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <button
                onClick={() => {
                  setMeetUrl(null);
                  setMeetExpanded(false);
                }}
                className="p-1.5 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <iframe
            src={meetUrl}
            width="100%"
            height={meetExpanded ? 'calc(100% - 48px)' : '352'}
            frameBorder="0"
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="bg-black"
            title="Google Meet"
          />
        </div>
      )}
    </div>
  );
}
